// =============================================================================
// src/chrome/copilot-sidebar/copilot-element-refs.ts
// Coleta unica das referencias de DOM da sidebar do Copiloto.
//
// Proveniencia: porte das chamadas document.getElementById(...) do IIFE inline
//   em unified.html linhas 2929-2946. Centralizar aqui evita re-consultar o DOM
//   espalhado e mantem os controllers (insight/collapse/scroll-spy) com uma unica
//   fonte de referencias — DI por argumento, sem getElementById escondido neles.
//
// Os ids sao os do markup de copilot-sidebar-markup.ts. Campos nulaveis espelham
//   o original (cada getById podia falhar; o IIFE faz guard antes de usar).
// =============================================================================

/**
 * Referencias de DOM do painel "Contexto atual" + controles globais da sidebar.
 * Espelha as constantes el* do IIFE original (unified.html 2933-2946).
 */
export interface CopilotElementRefs {
  readonly copilot: HTMLElement;
  readonly context: HTMLElement;
  readonly sectionLabel: HTMLElement | null;
  readonly dado: HTMLElement | null;
  readonly headline: HTMLElement | null;
  readonly detail: HTMLElement | null;
  readonly confBar: HTMLElement | null;
  readonly confLabel: HTMLElement | null;
  readonly actionPrimary: HTMLElement | null;
  readonly actionText: Element | null;
  readonly why: HTMLElement | null;
  readonly attentionChip: HTMLElement | null;
  readonly collapse: HTMLElement | null;
  readonly reopen: HTMLElement | null;
  readonly newChat: HTMLElement | null;
  /**
   * Itens de navegacao por secao. DESVIO PRESERVADO: o seletor original
   *   `.copilot-nav-item` NAO existe no markup da sidebar (unified.html 125-263),
   *   entao este array fica SEMPRE vazio — exatamente como no IIFE de origem
   *   (linha 2946). Mantido para replicar o comportamento (sem prefixo NN·, sem
   *   handlers de clique no nav-rail). Ver scroll-spy-observer.ts.
   */
  readonly navItems: readonly HTMLElement[];
}

/**
 * Coleta as referencias da sidebar. Devolve null se faltar #aiCopilot ou
 *   #copilotPanel — o IIFE original aborta nesse caso (unified.html 2931).
 */
export function collectCopilotElementRefs(): CopilotElementRefs | null {
  const copilot = document.getElementById('aiCopilot');
  const context = document.getElementById('copilotPanel');
  if (!copilot || !context) return null;

  const actionPrimary = document.getElementById('copilotActionPrimary');
  return {
    copilot,
    context,
    sectionLabel: document.getElementById('copilotSectionLabel'),
    dado: document.getElementById('copilotDado'),
    headline: document.getElementById('copilotHeadline'),
    detail: document.getElementById('copilotDetail'),
    confBar: document.getElementById('copilotConfBar'),
    confLabel: document.getElementById('copilotConfLabel'),
    actionPrimary,
    actionText: actionPrimary ? actionPrimary.querySelector('.copilot-action-text') : null,
    why: document.getElementById('copilotWhy'),
    attentionChip: document.getElementById('copilotAttentionChip'),
    collapse: document.getElementById('copilotCollapse'),
    reopen: document.getElementById('copilotReopen'),
    newChat: document.getElementById('copilotNewChat'),
    navItems: Array.from(
      document.querySelectorAll<HTMLElement>('.copilot-nav-item'),
    ),
  };
}
